<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useTranslation } from "i18next-vue";
import { Facade } from "@/drawing";
import type { TypedInputSpec } from "@/drawing/draggers/types";
import BasePointToolState from "@/stores/BasePointToolState";
import DraggerMenuState from "@/stores/DraggerMenuState";
import MeasurementKeypadState from "@/stores/MeasurementKeypadState";
import NumericKeypadModal, {
  type KeypadMode,
} from "@/components/canvas/Tools/NumericKeypadModal.vue";

/**
 * Headless controller wiring the floating keypad to whichever tool is active —
 * the draw tools (next vertex) and the base-point edit tools (MOVE / COPY /
 * ROTATE / SCALE) alike.
 *
 * One controller, because once both halves speak TypedInputSpec they have
 * nothing left to disagree about: a step advertises one or two modes, the pad
 * edits the active one's fields, and the values go back to the dragger tagged
 * with the mode they were typed in. This replaces the pair of panels that used
 * to split the job (and the mutual-exclusion guard that kept them from both
 * claiming the screen at once).
 */

const { t } = useTranslation();

function formatForField(value: number): string {
  return parseFloat(value.toFixed(4)).toString();
}

const spec = ref<TypedInputSpec | null>(null);
const values = ref<Record<string, string>>({});
const activeFieldKey = ref<string>("");

/**
 * Which fields the user has typed into since the last commit or step change.
 * Tracked per field, not as one flag, because it answers two questions at once:
 * whether to keep mirroring live drag values at all (any entry stops it, so a
 * stray hover cannot overwrite half-entered digits), and whether a given
 * field's next keystroke is still its first (see replacingFirstKeystroke).
 * Always reassigned rather than mutated, so reads stay reactive.
 */
const editedKeys = ref<Set<string>>(new Set());

/**
 * The mode actually in use. Reads through to the sticky session preference
 * whenever the current step offers it, and falls back to the step's first mode
 * otherwise — deliberately WITHOUT writing that fallback back to the store, so
 * a detour through ROTATE (angle-only) doesn't wipe a standing L/angle choice.
 */
const activeModeKey = computed<string>(() => {
  const modes = spec.value?.modes ?? [];
  if (modes.length === 0) return "";
  const preferred = modes.find(
    (mode) => mode.id === MeasurementKeypadState.modeId
  );
  return preferred?.id ?? modes[0].id;
});

const activeFields = computed(
  () =>
    spec.value?.modes.find((mode) => mode.id === activeModeKey.value)?.fields ??
    []
);

/** Modes handed to the pad, with their labels translated here. */
const keypadModes = computed<KeypadMode[]>(() =>
  (spec.value?.modes ?? []).map((mode) => ({
    key: mode.id,
    label: t("canvas:tools.options.keypad.mode." + mode.id),
    fields: mode.fields,
  }))
);

function onModeChange(key: string) {
  MeasurementKeypadState.modeId = key;
  // The fields now mean something else, so whatever was half-typed is void;
  // clearing lets the live values mirror straight back in.
  editedKeys.value = new Set();
  activeFieldKey.value =
    spec.value?.modes.find((mode) => mode.id === key)?.fields[0]?.key ?? "";
  mirrorValues(spec.value);
}

/**
 * Identity of the current step, used to tell "the tool advanced" from "the same
 * step emitted fresh live values". Computed over EVERY mode's fields, not just
 * the active one's, so switching mode is not mistaken for a step change (which
 * would wipe editedKeys mid-entry).
 */
function stepKey(next: TypedInputSpec | null): string {
  if (!next) return "";
  const fields = next.modes
    .map((mode) => mode.fields.map((field) => field.key).join(","))
    .join("|");
  return `${next.stepId ?? ""}#${fields}`;
}

function mirrorValues(next: TypedInputSpec | null) {
  if (!next) {
    values.value = {};
    return;
  }
  if (editedKeys.value.size > 0) return;
  const mirrored: Record<string, string> = {};
  for (const mode of next.modes) {
    for (const field of mode.fields) {
      // A value the step can't supply yet (a draw tool with no origin) shows
      // as an empty field rather than a misleading 0.
      const value = next.values[field.key];
      mirrored[field.key] = value === undefined ? "" : formatForField(value);
    }
  }
  values.value = mirrored;
}

function applySpec(next: TypedInputSpec | null) {
  const previous = spec.value;

  // A different set of fields means a different step (or a different tool):
  // whatever was being typed described the previous one, so drop it.
  if (stepKey(next) !== stepKey(previous)) {
    editedKeys.value = new Set();
    spec.value = next;
    activeFieldKey.value = activeFields.value[0]?.key ?? "";
  }

  // The base-point edit tools open the pad themselves once they reach a step
  // that accepts typing: nothing can be typed until the base point is down, so
  // that is the moment the pad becomes useful, and making the user go find the
  // toggle then is a step too many. Deliberately only on the null -> non-null
  // edge, so closing the pad mid-command sticks and a later step change (SCALE
  // going from factor to length) does not force it back open.
  //
  // The draw tools deliberately DON'T auto-open: a polyline vertex is normally
  // placed with the stylus, and having the pad appear over the canvas the
  // moment a first point goes down would be in the way far more often than not.
  // Their toggle button sits in the draw menu, so it stays opt-in there.
  if (!previous && next && BasePointToolState.active !== null) {
    MeasurementKeypadState.isOpen = true;
  }

  spec.value = next;
  mirrorValues(next);
}

// Drives the keyboard toggle button in SelectionMenu's in-progress bar, so it
// only shows up on a step that actually accepts typed values.
watch(spec, (next) => {
  BasePointToolState.canTypeInput = next !== null;
});

// Closing the pad without validating abandons any in-progress typed edit, so
// live drag values resume mirroring into the fields right away.
watch(
  () => MeasurementKeypadState.isOpen,
  (isOpen) => {
    if (!isOpen) editedKeys.value = new Set();
  }
);

let unsubscribe: (() => void) | undefined;
let initCheckInterval: ReturnType<typeof setInterval> | undefined;

function subscribeToTypedInput() {
  unsubscribe?.();
  unsubscribe = undefined;
  if (!Facade.isInitialized) return;
  const dragger = Facade.tools.current;
  if (!dragger) {
    applySpec(null);
    return;
  }
  applySpec(dragger.getTypedInputSpec());
  unsubscribe = dragger.onTypedInputChange((next) => applySpec(next));
}

// Facade.tools.current changes identity on two independent signals, and both
// have to re-subscribe: BasePointToolState for the edit tools (which never
// touch DraggerMenuState — see that store's doc comment), DraggerMenuState for
// the draw tools.
watch(
  () => BasePointToolState.active,
  (active) => {
    subscribeToTypedInput();
    // Nothing else closes the pad when an edit tool ends.
    if (active === null) MeasurementKeypadState.isOpen = false;
  }
);

watch(
  () => DraggerMenuState.activeDragger.name,
  () => {
    subscribeToTypedInput();
    MeasurementKeypadState.isOpen = false;
  }
);

// Leaving point-by-point mode takes the pad's reason to exist with it for the
// two-point tools: the engine drops its draft, so the spec goes null on its
// own, but the panel still has to stop showing an inert pad.
watch(
  () => DraggerMenuState.sequentialPointMode,
  () => {
    subscribeToTypedInput();
    MeasurementKeypadState.isOpen = false;
  }
);

onMounted(() => {
  subscribeToTypedInput();
  if (!Facade.isInitialized) {
    initCheckInterval = setInterval(() => {
      subscribeToTypedInput();
      if (Facade.isInitialized && initCheckInterval) {
        clearInterval(initCheckInterval);
        initCheckInterval = undefined;
      }
    }, 100);
  }
});

onBeforeUnmount(() => {
  unsubscribe?.();
  if (initCheckInterval) clearInterval(initCheckInterval);
});

/** The active mode's values as numbers, or null while any field is incomplete. */
function readValues(): Record<string, number> | null {
  const parsed: Record<string, number> = {};
  for (const field of activeFields.value) {
    const value = parseFloat(values.value[field.key] ?? "");
    if (Number.isNaN(value)) return null;
    parsed[field.key] = value;
  }
  return parsed;
}

/**
 * Every field arrives pre-filled from the live drag, so appending the first
 * keystroke to it would turn a field showing "0" into "01000" when the user
 * types 1000. The first keystroke after a mirror therefore replaces the
 * mirrored value instead of extending it; everything after that appends.
 *
 * The pad emits the whole new string rather than the key that was pressed, so
 * "a single character was appended" is recovered from the shape of the value.
 * Backspace (shorter) and the sign button (prepends "-") don't match and are
 * left alone.
 */
function replacingFirstKeystroke(key: string, value: string): string {
  if (editedKeys.value.has(key)) return value;
  const mirrored = values.value[key] ?? "";
  if (value.length !== mirrored.length + 1 || !value.startsWith(mirrored)) {
    return value;
  }
  return value.slice(mirrored.length);
}

function onFieldInput(key: string, value: string) {
  values.value = { ...values.value, [key]: replacingFirstKeystroke(key, value) };
  editedKeys.value = new Set(editedKeys.value).add(key);
  const parsed = readValues();
  if (parsed && Facade.isInitialized) {
    Facade.tools.current?.previewTypedInput(activeModeKey.value, parsed);
  }
}

function commit() {
  const parsed = readValues();
  if (!parsed || !Facade.isInitialized) return;
  // Let the tool advance first, then resume mirroring: for a repeating tool
  // (COPY, or a polyline gaining a vertex) the fields go back to tracking the
  // live state; for the others the tool ends and the pad closes.
  Facade.tools.current?.commitTypedInput(activeModeKey.value, parsed);
  editedKeys.value = new Set();
}
</script>

<template>
  <NumericKeypadModal
    v-if="spec"
    v-model:active-key="activeFieldKey"
    :title="$t(spec.titleKey)"
    :modes="keypadModes"
    :active-mode-key="activeModeKey"
    :hint-label="spec.hint ? $t(spec.hint.labelKey) : undefined"
    :hint-value="spec.hint ? formatForField(spec.hint.value) : undefined"
    :values="values"
    @update:active-mode-key="onModeChange"
    @field-input="onFieldInput"
    @validate="commit"
  />
</template>
